/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Claude Bridge panel (Settings → API Keys)
 *
 * Shows live status of the local Claude bridge, lets the user download a
 * ready-to-run copy of it, and explains how to run it. The bridge routes
 * Claude calls through the user's Claude Code login so usage bills their
 * Claude Pro/Max subscription instead of API credits.
 */

import { useCallback, useEffect, useState } from 'react'
import { Download, RefreshCw, Zap } from 'lucide-react'
import JSZip from 'jszip'
import { checkBridgeAvailable, getBridgeUrl } from '@/providers/claude-bridge'
// Bundled at build time so the deployed app can hand out the bridge source
import bridgeServerSource from '../../../bridge/server.mjs?raw'

const AGENT_SDK_VERSION = '^0.3.0'

function buildBridgeZip(): Promise<Blob> {
  const origin = window.location.origin
  const isLocal = origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1')

  const packageJson = {
    name: 'hivemind-claude-bridge',
    version: '1.0.0',
    private: true,
    type: 'module',
    description:
      'Local bridge that routes Hivemind Claude calls through your Claude Code login (Pro/Max subscription billing)',
    scripts: {
      start: 'node server.mjs',
    },
    engines: { node: '>=18' },
    dependencies: {
      '@anthropic-ai/claude-agent-sdk': AGENT_SDK_VERSION,
    },
  }

  const readme = `# Hivemind Claude Bridge

Routes Hivemind's Claude calls through your Claude Code login, so usage
bills your **Claude Pro/Max subscription** instead of Anthropic API credits.

For personal use only — don't expose this bridge to other people; their
usage would ride on your subscription, which violates Anthropic's terms.

## Requirements

1. **Node.js 18+** — https://nodejs.org
2. **Claude Code**, logged in with your Pro/Max account — https://claude.com/claude-code
   (run \`claude\` in a terminal once and log in; check with \`/status\`)

## Setup (one time)

Unzip this folder anywhere, then in a terminal inside it:

\`\`\`sh
npm install
\`\`\`

## Run

\`\`\`sh
npm start
\`\`\`

Leave the terminal open while you use Hivemind. The app detects the bridge
automatically within ~30 seconds — Claude calls will show up in Hivemind's
logs as "Claude Bridge (Max)" with $0 cost. Stop the bridge (Ctrl+C) and the
app falls back to your Anthropic API key.

${isLocal ? '' : `## Your Hivemind origin

You downloaded this from \`${origin}\`. The bridge allows requests from
\`https://huikku.github.io\` and any localhost origin by default. If your
Hivemind runs somewhere else, start the bridge with:

\`\`\`sh
BRIDGE_ALLOWED_ORIGINS=${origin} npm start
\`\`\`

(Windows PowerShell: \`$env:BRIDGE_ALLOWED_ORIGINS='${origin}'; npm start\`)

`}## Notes

- Tool/function calls are not supported through the bridge — Hivemind sends
  those to the Anthropic API directly, so keep your API key configured if
  your flows use tools.
- Claude model ids are mapped to your subscription's sonnet / opus / haiku.
- Usage counts against your subscription's normal rate limits, shared with
  Claude Code.
- The bridge listens on http://localhost:8787 (change with BRIDGE_PORT).
`

  const zip = new JSZip()
  zip.file('server.mjs', bridgeServerSource)
  zip.file('package.json', JSON.stringify(packageJson, null, 2) + '\n')
  zip.file('README.md', readme)
  return zip.generateAsync({ type: 'blob' })
}

export function ClaudeBridgePanel() {
  const [status, setStatus] = useState<'checking' | 'online' | 'offline'>('checking')
  const [downloading, setDownloading] = useState(false)

  const refresh = useCallback(async (force = false) => {
    setStatus('checking')
    const online = await checkBridgeAvailable(force)
    setStatus(online ? 'online' : 'offline')
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const blob = await buildBridgeZip()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'hivemind-claude-bridge.zip'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="border-t border-accent/30 pt-4 mt-4">
      <h4 className="text-sm font-title mb-2 flex items-center gap-2">
        <Zap className="w-4 h-4 text-accent" />
        Claude Bridge — use your Claude Pro/Max subscription
      </h4>
      <p className="text-xs text-foreground-muted mb-3">
        Run a small bridge on your computer to route Claude calls through your Claude Code login.
        Chat usage then bills your Claude subscription instead of API credits ($0 marginal cost).
        Without the bridge, Claude uses your Anthropic API key as usual.
      </p>

      {/* Status row */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-block w-2 h-2 rounded-full ${
            status === 'online'
              ? 'bg-green-500'
              : status === 'checking'
                ? 'bg-yellow-500 animate-pulse'
                : 'bg-gray-500'
          }`}
        />
        <span className="text-xs text-foreground-secondary">
          {status === 'online' && (
            <>Bridge connected at {getBridgeUrl()} — Claude calls use your subscription</>
          )}
          {status === 'offline' && <>Bridge not detected — Claude calls use your API key</>}
          {status === 'checking' && <>Checking for bridge…</>}
        </span>
        <button
          type="button"
          onClick={() => refresh(true)}
          className="p-1 hover:bg-background-tertiary rounded transition-colors"
          title="Re-check bridge status"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${status === 'checking' ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-2"
      >
        <Download className="w-3.5 h-3.5" />
        {downloading ? 'Packaging…' : 'Download bridge (.zip)'}
      </button>

      <div className="bg-background-tertiary/50 border border-border rounded-lg p-3 mt-3">
        <p className="text-xs font-medium text-foreground-secondary mb-2">How to run it</p>
        <ol className="text-xs text-foreground-muted space-y-1 list-decimal list-inside">
          <li>
            Install <a href="https://nodejs.org" target="_blank" rel="noreferrer" className="text-accent hover:underline">Node.js 18+</a> and{' '}
            <a href="https://claude.com/claude-code" target="_blank" rel="noreferrer" className="text-accent hover:underline">Claude Code</a>{' '}
            (log in with your Pro/Max account)
          </li>
          <li>Unzip the download, open a terminal in the folder</li>
          <li>
            Run <code className="code-block px-1">npm install</code> once, then{' '}
            <code className="code-block px-1">npm start</code>
          </li>
          <li>Leave it running — this page detects it within ~30s (or hit refresh above)</li>
        </ol>
        <p className="text-xs text-foreground-muted mt-2">
          Personal use only: the bridge spends <em>your</em> subscription, so don't share it or expose
          it beyond your own machine. Tool-calling requests still use your API key.
        </p>
      </div>
    </div>
  )
}
